import { useTranslation } from 'react-i18next';
import { useTour } from '../hooks/useTour';
import { TourStep } from '../types/TourTypes';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Progress } from '@/shared/components/ui/progress';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useUserSettingsByUserId } from '@/shared/hooks/api/useUserSettings';

export const TourChecklist = () => {
  const { t, i18n } = useTranslation();
  const tour = useTour();
  const { tourState, allSteps, currentStep, closeTour } = tour!;
  const { user } = useAuth();
  const { data: userSettings } = useUserSettingsByUserId(user?.id);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const currentLang = i18n.language as 'pt' | 'en';

  // Calcula progresso baseado no step do usuário
  const currentStepIndex = userSettings?.metadata?.tour?.step ?? 0;
  const completedCount = currentStepIndex > 0 ? currentStepIndex : (tourState.completedSteps.length);
  const progress = (completedCount / allSteps.length) * 100;

  const getStepName = (step: typeof allSteps[0]) => {
    return step.name[currentLang] || step.name['pt'];
  };

  const getStepObjective = (step: typeof allSteps[0]) => {
    return step.objective[currentLang] || step.objective['pt'];
  };

  return (
    <Card className="fixed bottom-4 right-4 w-64 shadow-lg z-[10001] tour-checklist-enter">
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-1.5">
            {t('tour.checklist.title')}
            <span className="text-[10px] font-normal text-muted-foreground">
              ({completedCount}/{allSteps.length})
            </span>
          </CardTitle>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={closeTour}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1.5 mt-1.5" />
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="pt-0 px-3 pb-3">
          <div className="space-y-2 tour-checklist-scroll">
            {allSteps.map((step: TourStep, index: number) => {
              // Step está completo se o índice for menor que o step atual do usuário
              const isCompleted = index < currentStepIndex || tourState.completedSteps.includes(step.id);
              const isCurrent = currentStep?.id === step.id;

              return (
                <div
                  key={step.id}
                  className={`p-2 rounded-md border transition-all ${isCurrent
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-card'
                    }`}
                >
                  <div className="flex items-start gap-1.5">
                    <Checkbox
                      checked={isCompleted}
                      className="mt-0.5 h-3 w-3"
                      disabled
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-medium text-muted-foreground">
                          {index + 1}.
                        </span>
                        <p className={`text-xs font-medium leading-tight ${isCompleted ? 'line-through text-muted-foreground' : ''
                          }`}>
                          {getStepName(step)}
                        </p>
                      </div>
                      {isCurrent && (
                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                          {getStepObjective(step)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
